import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';

type ConfirmVariant = 'danger' | 'warning' | 'default';

interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description?: string | ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: ConfirmVariant;
    isLoading?: boolean;
    onConfirm: () => void;
}

const variantConfig = {
    danger: {
        icon: <Trash2 className="w-5 h-5 text-red-500" />,
        bg: 'bg-red-50',
        border: 'border-red-100',
        confirmClass: 'bg-red-600 hover:bg-red-700 text-white',
    },
    warning: {
        icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
        bg: 'bg-amber-50',
        border: 'border-amber-100',
        confirmClass: 'bg-amber-600 hover:bg-amber-700 text-white',
    },
    default: {
        icon: <CheckCircle2 className="w-5 h-5 text-blue-500" />,
        bg: 'bg-blue-50',
        border: 'border-blue-100',
        confirmClass: 'bg-[#0f172a] hover:bg-[#0a1120] text-white',
    },
};

export function ConfirmDialog({
    open,
    onOpenChange,
    title,
    description,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    variant = 'default',
    isLoading = false,
    onConfirm,
}: ConfirmDialogProps) {
    const cfg = variantConfig[variant];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${cfg.bg} border ${cfg.border} mb-1`}>
                        {cfg.icon}
                        <DialogTitle className="text-sm font-semibold text-gray-800">{title}</DialogTitle>
                    </div>
                </DialogHeader>
                {description && (
                    typeof description === 'string'
                        ? <p className="text-sm text-gray-500 px-1">{description}</p>
                        : <div className="text-sm text-gray-500 px-1">{description}</div>
                )}
                <DialogFooter className="gap-2 sm:gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        size="sm"
                        className={cfg.confirmClass}
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
